/**
 * 隱私權政策與個人資料使用同意書 · v1.0（2026-08-31 生效）
 * 內容 SSOT：系統設計/隱私權政策與個資使用同意書.md
 *
 * MVP：內容直接 render（中文 · 台灣 PDPA baseline）。原本用 legal.privacy.section* i18n key
 * 但那些 key 未 seed → 顯示 raw key（porsche 2026-08-19「資料是錯的」）→ 改直接 render 條款內容。
 * 多語（zh-CN/en）i18n 化列 Task-32-B follow-up。
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { Box, Container, Typography, Divider, Button, Paper } from '@mui/material';

const VERSION = 'v1.0';
const EFFECTIVE = '2026-08-31';
const LAST_UPDATED = '2026-08-19';

interface Sec { no: string; title: string; body: string; }

const SECTIONS: Sec[] = [
  {
    no: '一', title: '前言與適用範圍',
    body:
      '歡迎使用本後臺管理基底平台（以下簡稱「本平台」）。本平台由服務提供者（以下簡稱「我方」）開發並營運，提供予各租戶組織（以下簡稱「租戶」）進行內部業務管理使用。\n\n' +
      '我方尊重並致力於保障您的個人資料權益。本政策依據中華民國個人資料保護法（以下簡稱「個資法」）及相關法規制定，說明我方在您使用本平台服務時，如何蒐集、處理及利用您的個人資料。\n\n' +
      '本政策適用於：\n' +
      '• 所有透過本平台網頁介面、行動網頁應用（PWA）、API 存取本平台之使用者\n' +
      '• 由租戶內管理員為您建立之帳號\n' +
      '• 透過第三方登入（如 LINE 登入）綁定本平台之使用者\n\n' +
      '本政策不適用於：\n' +
      '• 本平台以外之其他網站（即使有連結）\n' +
      '• 租戶內部自訂之其他系統或服務',
  },
  {
    no: '二', title: '個人資料蒐集項目',
    body:
      '2.1 · 帳號基本資料（必要）\n' +
      '• 帳號名稱、密碼（加密儲存，我方無法查看明碼）\n' +
      '• 姓名、電子郵件\n' +
      '• 所屬租戶組織、角色與權限\n' +
      '• 部門、職稱、聯絡電話（選填）\n\n' +
      '2.2 · 第三方登入資訊（選擇性）\n' +
      '• 若您使用 LINE 登入綁定：LINE User ID、LINE 顯示名稱、頭像 URL\n' +
      '• 我方不會取得您 LINE 帳號的密碼或好友清單\n\n' +
      '2.3 · 操作與使用紀錄（自動蒐集）\n' +
      '• 登入時間、來源 IP、瀏覽器類型、裝置類型\n' +
      '• 系統操作日誌（如新增、修改、刪除、查詢紀錄）\n' +
      '• 上傳檔案的建立／修改／刪除時間、檔案類型與大小\n' +
      '• Session 建立、更新與登出時間\n' +
      '• API 呼叫紀錄（用於稽核與異常偵測）\n\n' +
      '2.4 · 業務資料（依租戶用途）\n' +
      '• 您在本平台建立或編輯的業務資料（如行事曆事件、聯絡人、通訊資料、上傳檔案內容）\n' +
      '• 您與 AI 助理的對話內容與語音錄音（若使用該功能）\n' +
      '• 會議紀錄的錄音與轉譯（若使用會議紀錄功能，將另行取得所有參與者同意）',
  },
  {
    no: '三', title: '蒐集目的',
    body:
      '我方蒐集您的個人資料，目的如下：\n\n' +
      '1. 提供本平台核心服務：身分驗證、權限管理、業務資料管理、跨模組協作\n' +
      '2. 系統安全與稽核合規：偵測異常登入、防止未授權存取、稽核操作紀錄\n' +
      '3. 服務改善與效能監控：分析使用行為以優化功能與 UI（不會用於個人識別）\n' +
      '4. 資訊安全巡檢：AI 主動巡檢資安風險（如惡意檔案、異常行為），保護您與租戶資料安全\n' +
      '5. 通知與客戶服務：發送系統通知、事件提醒、服務公告\n' +
      '6. 法定義務履行：依法律規範保留必要紀錄',
  },
  {
    no: '四', title: '資料利用範圍、期間、地區與對象',
    body:
      '4.1 · 利用範圍與期間\n' +
      '• 利用範圍：僅限於前述蒐集目的相關業務\n' +
      '• 利用期間：自您註冊使用本平台起，至您停止使用並要求刪除資料後 3 個月（供資料還原之緩衝期，超過 3 個月後永久刪除）\n\n' +
      '4.2 · 利用地區\n' +
      '• 主要儲存：中華民國境內雲端伺服器\n' +
      '• 可能傳輸至：您所選 AI 服務提供者所在地區（若您租戶啟用 BYO AI 功能，請見第五節）\n\n' +
      '4.3 · 利用對象\n' +
      '• 我方：平台營運、技術維運人員（限最少必要範圍）\n' +
      '• 您所屬租戶：租戶內管理員可依權限存取您在該租戶內的業務資料\n' +
      '• 第三方服務（必要時，見第五節）\n' +
      '• 主管機關：依法律要求時提供',
  },
  {
    no: '五', title: '第三方服務與資料處理者',
    body:
      '本平台在提供服務時，可能將部分資料傳輸至以下第三方，均已審慎評估其資訊安全：\n\n' +
      '5.1 · AI 服務提供者（BYO · 由租戶自選）\n' +
      '• 若您所屬租戶啟用 AI 功能（語音助理、業務問答、資安巡檢等），您與 AI 的對話內容將傳送至租戶自選的 AI 服務提供者（如 OpenAI、Anthropic、Google 等）\n' +
      '• 重要：AI 服務提供者由租戶決定並自行付費使用，我方僅提供技術橋接\n' +
      '• 具體 AI 提供者名稱、其個資政策、資料留存期，請洽詢您所屬租戶管理員\n' +
      '• 若您不同意特定 AI 提供者，請與租戶管理員協商停用 AI 功能\n\n' +
      '5.2 · LINE 登入服務（選擇性）\n' +
      '• 若您使用 LINE 登入，將依 LINE Corporation 之隱私權政策處理相關授權資訊\n' +
      '• LINE 隱私權政策：https://terms.line.me/line_rules\n\n' +
      '5.3 · 雲端服務基礎設施\n' +
      '• 儲存、運算、網路服務由專業雲端服務商提供，合約要求其符合資訊安全標準\n' +
      '• 我方對雲端服務商的資料存取有嚴格控管，雲端服務商不會取得您的資料明文內容（見第六節加密）\n\n' +
      '5.4 · 電子郵件寄送\n' +
      '• 系統通知信件可能透過第三方郵件服務寄送\n' +
      '• 僅傳送必要之收件人與通知內容，不會分享其他個人資料',
  },
  {
    no: '六', title: '資料儲存與安全防護',
    body:
      '6.1 · 加密機制（核心保護）\n' +
      '• 靜態加密（at-rest）：您上傳的所有檔案內容，在儲存前均採用 AES-256-GCM 加密\n' +
      '• 金鑰保護：採用平台主金鑰（Master Key）包裹租戶金鑰、個人金鑰的兩層設計\n' +
      '• 主金鑰隔離：主金鑰以離線方式管理，雲端服務商亦無法解密您的檔案內容\n' +
      '• 傳輸加密（in-transit）：所有 API 通訊均採用 TLS 1.2+ 加密\n\n' +
      '6.2 · 存取控制\n' +
      '• 採用角色權限管理（RBAC），每個帳號僅能存取被授權的資料\n' +
      '• 跨租戶資料完全隔離，租戶間無法互相查看\n' +
      '• 平台管理人員存取生產資料需經雙人核可\n\n' +
      '6.3 · 稽核與監控\n' +
      '• 所有敏感操作（登入、資料修改、權限異動）均記錄於稽核日誌\n' +
      '• AI 主動巡檢異常行為（如短時間大量下載、非常規時段登入等）並警示租戶管理員\n' +
      '• 定期由 AI 資安巡檢比對 CVE 資料庫，主動修補元件\n\n' +
      '6.4 · 資料保留期\n' +
      '• 業務資料：租戶關係存續期間 + 停用後 3 個月緩衝期，之後永久刪除\n' +
      '• 操作日誌：保留 1 年供稽核使用，之後歸檔或刪除\n' +
      '• 加密金鑰輪換：季度輪換一次',
  },
  {
    no: '七', title: '當事人權益',
    body:
      '依個資法第 3 條，您就本平台蒐集之個人資料，享有以下權利：\n\n' +
      '1. 查詢或請求閱覽：查看您在本平台上的個人資料\n' +
      '2. 請求製給複製本：取得您資料的可攜出格式\n' +
      '3. 請求補充或更正：更新或修正錯誤資料（可透過帳號設定自行變更部分欄位）\n' +
      '4. 請求停止蒐集、處理或利用\n' +
      '5. 請求刪除\n\n' +
      '7.1 · 行使權利之方式\n' +
      '• 請透過本平台「個人設定」頁面自行調整可修改欄位\n' +
      '• 或透過所屬租戶管理員協助處理\n' +
      '• 對租戶處理有疑義時，可聯絡我方（見第十節）\n\n' +
      '7.2 · 拒絕提供之影響\n' +
      '• 若您拒絕提供必要資料（如帳號、密碼），我方將無法為您提供服務\n' +
      '• 選擇性資料（如 LINE 綁定）拒絕不影響核心功能使用',
  },
  {
    no: '八', title: 'Cookie、Session 與追蹤技術',
    body:
      '• 本平台使用 Cookie 及 Redis Session 維持登入狀態\n' +
      '• Session 有效時間預設 4 小時，可透過使用者活動延展\n' +
      '• 我方不使用第三方廣告追蹤 Cookie\n' +
      '• 我方不將您的行為資料販售予第三方\n\n' +
      '您可透過瀏覽器設定管理 Cookie，但停用可能導致無法正常登入。',
  },
  {
    no: '九', title: '未成年條款',
    body:
      '本平台為企業內部管理系統，設計上不供 18 歲以下未成年人使用。若您未滿 18 歲，請由法定代理人代為判斷是否適合使用，並取得其同意。',
  },
  {
    no: '十', title: '政策變更',
    body:
      '我方保留隨時修改本政策之權利。重大變更將於本頁面公告，並於您下次登入時再次要求同意。\n\n' +
      '• 本次版本：v1.0（2026-08-31 生效）\n' +
      '• 前次版本：無（首次發布）',
  },
  {
    no: '十一', title: '聯絡窗口',
    body:
      '如您對本政策有任何疑問、意見，或欲行使當事人權益，請透過以下方式聯絡：\n\n' +
      '• 聯絡窗口：您所屬租戶之系統管理員（最快回應）\n' +
      '• 回應期限：自收到請求之次一營業日起 15 個工作日內回覆',
  },
  {
    no: '十二', title: '準據法與管轄',
    body:
      '本政策依中華民國法律解釋、規範，涉本政策爭議時，以臺灣臺北地方法院為第一審管轄法院。',
  },
];

const PrivacyPage: React.FC = () => (
  <Container maxWidth="md" sx={{ py: 6 }}>
    <Typography variant="h4" gutterBottom fontWeight={700}>
      隱私權政策與個人資料使用同意書
    </Typography>
    <Typography variant="body2" color="text.secondary">
      版本 {VERSION}｜生效日期 {EFFECTIVE}｜上次更新 {LAST_UPDATED}
    </Typography>
    <Divider sx={{ my: 3 }} />

    {SECTIONS.map((s) => (
      <Box key={s.no} mb={4}>
        <Typography variant="h6" gutterBottom fontWeight={600}>
          {s.no}、{s.title}
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ whiteSpace: 'pre-line', lineHeight: 1.9 }}>
          {s.body}
        </Typography>
      </Box>
    ))}

    <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50', mb: 3 }}>
      <Typography variant="body2" fontWeight={600}>
        同意即表示：您已閱讀、理解並同意本政策全部內容，並授權我方依本政策蒐集、處理及利用您的個人資料。
      </Typography>
    </Paper>

    <Button component={Link} to="/login" variant="outlined">
      返回登入
    </Button>
  </Container>
);

export default PrivacyPage;
